import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import Dropzone from 'react-dropzone';

import Button from '@/components/Button';
import GeneralLoading from '@/components/GeneralLoading';

interface PreviewProps {
  meta: any;
  index: number;
  deleteFile: (index: number) => void;
}

const DropzoneMultiImage = ({
  setImages,
  images,
}: {
  setImages: (images: File[] | ((prev: File[]) => File[])) => void;
  images: File[];
}) => {
  const initialImages = useMemo(() => {
    return images.map((file) => ({
      ...file,
      path: file instanceof File ? URL.createObjectURL(file) : '',
    }));
  }, [images]);

  const [filesAccepted, setFilesAccepted] = useState<File[]>(initialImages);
  const loading = false;

  const handleFilesAccepted = (newFile: File[]) => {
    const newFilesCopy = newFile.map((file) => ({
      ...file,
      path: URL.createObjectURL(file),
    }));
    setFilesAccepted((prevFiles) => [...prevFiles, ...newFilesCopy]);
    setImages((prevImages) => [...prevImages, ...newFile]);
  };

  const deleteFile = (index: number) => {
    const newFiles = filesAccepted.filter((_, i) => i !== index);
    setFilesAccepted(newFiles);
    setImages(newFiles);
  };

  return (
    <>
      <div style={{ marginTop: '1rem' }}>
        {loading ? (
          <GeneralLoading size={100} text="Cargando" withDots fontVariant="h5" />
        ) : (
          <Dropzone
            onDrop={(acceptedFiles: File[]) => handleFilesAccepted(acceptedFiles)} // Aca es donde se reciben los archivos que se suben, el formato es "File[]", osea un array de File
            accept={{ 'image/*': ['.jpg', '.jpeg', '.png'] }} // Formatos aceptados
            // onDropAccepted={() => setDropAccepted(true)} // Aca se ejecuta la funcion que se encarga de subir los archivos
          >
            {({ getRootProps, getInputProps }) => (
              <section>
                {filesAccepted.length > 0 ? (
                  <Grid
                    container
                    sx={{
                      display: 'flex',
                      alignItems: 'start',
                      justifyContent: 'center',
                      padding: '1rem',
                      gap: '0.5rem',
                    }}
                  >
                    {filesAccepted.map((file, index) => (
                      <Preview key={index} meta={file} index={index} deleteFile={deleteFile} />
                    ))}
                    <div {...getRootProps()}>
                      <input {...getInputProps()} />
                      <InputNewFiles />
                    </div>
                  </Grid>
                ) : (
                  <div {...getRootProps()}>
                    <input {...getInputProps()} />
                    <InputContent />
                  </div>
                )}
              </section>
            )}
          </Dropzone>
        )}
      </div>
    </>
  );
};

export default DropzoneMultiImage;

const InputContent = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        border: '1px dashed',
        borderColor: theme.palette.grey[500],
        padding: '1rem',
        borderRadius: '0.5rem',
        gap: '0.5rem',
        backgroundColor: theme.palette.grey[50],
        cursor: 'pointer',
      }}
    >
      <Button variant="outlined" startIcon={<AddPhotoAlternateIcon color="primary" />}>
        ¡Selecciona tus fotos JPG, JPEG o PNG aquí!
      </Button>

      <Typography variant="body2" color={theme.palette.secondary.main}>
        O arrastra tus fotos aquí
      </Typography>
    </Box>
  );
};

const Preview = ({ meta, index, deleteFile }: PreviewProps) => {
  const { path } = meta;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
      <img
        src={path}
        style={{ width: '95px', height: '95px', borderRadius: '1rem', objectFit: 'cover' }}
      />
      <Button onClick={() => deleteFile(index)} size="small" variant="outlined" color="error">
        <DeleteOutlineIcon color="error" />
      </Button>
    </Box>
  );
};

// const Layout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }) => {
//   const itemsPerPage = 14;
//   const [currentItems, setCurrentItems] = useState(null);
//   const [pageCount, setPageCount] = useState(0);
//   const [itemOffset, setItemOffset] = useState(0);

//   useEffect(() => {
//     const endOffset = itemOffset + itemsPerPage;
//     setCurrentItems(previews.slice(itemOffset, endOffset));
//     setPageCount(Math.ceil(previews.length / itemsPerPage));
//   }, [itemOffset, itemsPerPage, previews]);

//   const handlePageClick = (event) => {
//     const newOffset = (event.selected * itemsPerPage) % previews.length;
//     setItemOffset(newOffset);
//   };

//   return (
//     <div
//       {...dropzoneProps}
//       style={{
//         ...dropzoneProps.style,
//         backgroundColor: files.length > 0 ? '#fff' : '#ebebeb',
//         borderStyle: files.length > 0 ? 'none' : 'dashed',
//         borderColor: files.length > 0 ? 'none' : '#c3c3c3',
//         padding: '0',
//         justifyContent: files.length > 0 ? 'flex-start' : 'center',
//       }}
//     >
//       {currentItems}
//       {files.length < maxFiles && input}
//       {files.length > 0 && submitButton}
//     </div>
//   );
// };

const InputNewFiles = () => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        width: '95px',
        height: '95px',
        border: '1px dashed',
        borderColor: theme.palette.primary.main,
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0.5rem',
      }}
    >
      <AddAPhotoIcon color="primary" fontSize="small" />
      <Typography variant="caption" textAlign="center" color={theme.palette.grey[500]}>
        <strong>Agrega o arrastra tus fotos aquí</strong>
      </Typography>
    </Box>
  );
};
